import {useInboxBacklogSummaryQuery} from '@transactions/graphql';
import {parseDate} from 'common/date';
import {ChevronRight} from 'lucide-react';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {useBreadcrumb} from 'src/Breadcrumbs';
import Empty from 'src/ListView/Empty';
import Loading from 'src/Loading';
import type {InboxBacklogSummaryQuery} from '@transactions/graphql';

type YearBucket =
  InboxBacklogSummaryQuery['inboxBacklogSummary']['years'][number];

// Years at or above this coverage read as "finish off the remainder"; below
// it they read as "this year was never really reviewed".
const NEARLY_FINISHED_COVERAGE = 0.8;

const coverage = (bucket: YearBucket): number =>
  bucket.total > 0 ? (bucket.total - bucket.unallocated) / bucket.total : 0;

const YearRow = ({
  bucket,
  frontierDate,
}: {
  bucket: YearBucket;
  frontierDate: string | null;
}) => {
  const percent = Math.floor(coverage(bucket) * 100);
  // If the frontier falls inside this year, stop the drill-in the day
  // before it so the list matches the backlog count
  const yearEnd =
    frontierDate !== null && frontierDate.startsWith(`${bucket.year}-`)
      ? (parseDate(frontierDate).minus({days: 1}).toISODate() ?? `${bucket.year}-12-31`)
      : `${bucket.year}-12-31`;
  return (
    <Link
      to={`/ledger?layer=activity&view=review&start=${bucket.year}-01-01&end=${yearEnd}`}
      className="border-border hover:bg-muted/50 grid cursor-pointer grid-cols-[3.5rem_1fr_auto_auto] items-center gap-3 border-b px-4 py-3 transition-colors"
    >
      <span className="font-semibold tabular-nums">{bucket.year}</span>
      <span className="bg-muted h-1.5 overflow-hidden rounded-full">
        <span
          className="block h-full rounded-full bg-green-700 dark:bg-green-500"
          style={{width: `${percent}%`}}
        />
      </span>
      <span className="text-muted-foreground text-right text-sm whitespace-nowrap tabular-nums">
        {bucket.unallocated.toLocaleString('en-US')} of{' '}{bucket.total.toLocaleString('en-US')} · {percent}% done
      </span>
      <ChevronRight className="text-muted-foreground size-4" />
    </Link>
  );
};

const SectionHeading = ({children}: {children: React.ReactNode}) => (
  <div className="text-muted-foreground px-4 pt-6 pb-2 text-xs font-semibold tracking-wide uppercase">
    {children}
  </div>
);

// Drill-in view behind the inbox's backlog card: the historical pile of
// unallocated raw transactions, grouped by year with coverage bars. A year
// row opens the inbox scoped to that year, so chipping away reuses the
// normal review workflow.
export const BacklogPage = () => {
  useBreadcrumb({
    label: 'Backlog',
    fallback: [{label: 'Inbox', href: '/'}],
  });

  const {data, loading, error, refetch} = useInboxBacklogSummaryQuery({
    fetchPolicy: 'network-only',
  });
  const summary = data?.inboxBacklogSummary;

  if (loading && summary === undefined) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading size="medium" />
      </div>
    );
  }

  if (summary === undefined && error) {
    return (
      <div className="flex flex-col items-center gap-2 py-8">
        <span className="text-muted-foreground text-sm">
          Couldn&apos;t load the backlog.
        </span>
        <button
          type="button"
          className="text-foreground cursor-pointer text-sm underline"
          onClick={() => void refetch()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (summary === undefined || summary.backlogCount === 0) {
    return <Empty>No backlog — all historical activity is processed.</Empty>;
  }

  const processedPercent =
    summary.totalCount > 0
      ? Math.round((summary.processedCount / summary.totalCount) * 100)
      : 0;

  // Newest years first within each group: recent remainders are the most
  // likely to actually get finished.
  const yearsNewestFirst = [...summary.years].sort((a, b) => b.year - a.year);
  const nearlyFinished = yearsNewestFirst.filter(
    (bucket) => coverage(bucket) >= NEARLY_FINISHED_COVERAGE,
  );
  const mostlyUnreviewed = yearsNewestFirst.filter(
    (bucket) => coverage(bucket) < NEARLY_FINISHED_COVERAGE,
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="px-4 pt-6">
        <div className="text-2xl font-semibold">
          {summary.backlogCount.toLocaleString('en-US')} remaining
        </div>
        <div className="text-muted-foreground mt-0.5 text-sm">
          {summary.backlogOldestDate} – {summary.backlogNewestDate} · nothing
          newer is outstanding
        </div>
        <div className="bg-muted mt-4 h-1.5 overflow-hidden rounded-full">
          <div
            className="h-full rounded-full bg-green-700 dark:bg-green-500"
            style={{width: `${processedPercent}%`}}
          />
        </div>
        <div className="text-muted-foreground mt-1.5 text-xs">
          {processedPercent}% of all{' '}
          {summary.totalCount.toLocaleString('en-US')} historical raw
          transactions processed
        </div>
      </div>

      {nearlyFinished.length > 0 && (
        <div>
          <SectionHeading>Nearly finished years</SectionHeading>
          {nearlyFinished.map((bucket) => (
            <YearRow key={bucket.year} bucket={bucket} frontierDate={summary.frontierDate ?? null} />
          ))}
        </div>
      )}

      {mostlyUnreviewed.length > 0 && (
        <div>
          <SectionHeading>Mostly unreviewed</SectionHeading>
          {mostlyUnreviewed.map((bucket) => (
            <YearRow key={bucket.year} bucket={bucket} frontierDate={summary.frontierDate ?? null} />
          ))}
        </div>
      )}
    </div>
  );
};
